import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/utils/cn';
import type { ClaimRow } from '../types/claims.types';
import { summarizeReasons } from '../utils/claim-filters';
import { ClaimCells } from './ClaimCells';

interface ClaimRowGroupProps {
  readonly claim: ClaimRow;
  /** The lines listed when expanded; defaults to all of the claim's lines. */
  readonly lines?: readonly ClaimRow[];
  readonly selected: boolean;
  onSelect: (claim: ClaimRow) => void;
}

/**
 * One claim and, when expanded, its line entries (US-009 AC-004). Expansion is local to the row, so
 * opening one never collapses or expands another.
 */
export function ClaimRowGroup({
  claim,
  lines = claim.lines,
  selected,
  onSelect,
}: ClaimRowGroupProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className={cn('border-line hover:bg-brand-50 border-b', selected && 'bg-brand-50')}>
        {/* Centred, like every other cell in the row: a wrapping Provider Name or Reason makes the row
            two or three lines tall, and `align-top` left the toggle stranded at the top of it. */}
        <td className="px-2 py-2 align-middle">
          {claim.lines.length > 0 && (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={`Claim lines for ${claim.claimNumber}`}
              onClick={(): void => {
                setExpanded((current) => !current);
              }}
              // `flex` rather than the default inline box: an inline button sits on the text baseline,
              // which left the icon a few pixels above the centre even with the cell aligned middle.
              className="text-muted hover:text-brand-600 flex"
            >
              {expanded ? (
                <ChevronDown aria-hidden="true" className="h-4 w-4" />
              ) : (
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
              )}
            </button>
          )}
        </td>

        <td className="px-3 py-2">
          <button
            type="button"
            onClick={(): void => {
              onSelect(claim);
            }}
            className="text-brand-500 underline"
          >
            {claim.claimNumber}
          </button>
        </td>
        <ClaimCells claim={{ ...claim, reason: claim.reason ?? summarizeReasons(lines) }} />
      </tr>

      {expanded &&
        lines.map((line) => (
          <tr
            key={`${claim.claimNumber}-${String(claim.lines.indexOf(line))}`}
            className="border-line border-b bg-surface-muted/60"
          >
            <td />
            <td className="px-3 py-2 text-faint">Line {claim.lines.indexOf(line) + 1}</td>
            <ClaimCells claim={line} />
          </tr>
        ))}
    </>
  );
}
